// src/pages/FrameIQCreate.js

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast, { Toaster } from 'react-hot-toast';

const FrameIQCreate = () => {
  // ==================== STATE MANAGEMENT ====================
  
  // Form data state
  const [formData, setFormData] = useState({
    brief_name: '',
    ad_creative: '',
    keywords: '',
    geo_country: '',
    city: '',
    language: '',
    audience: '',
    output_mode: 'direct',
    type: 'keyword',
    keywords_type: 'text',
    remarks: '',
    dv360_config_name: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [showDV360Config, setShowDV360Config] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');
  
  // Navigation
  const navigate = useNavigate();
  
  // API base URL
  const BASE_URL = process.env.REACT_APP_API_URL;

  // ==================== HANDLERS ====================
  
  // Handle text input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Handle Mode of Output change (show/hide DV360)
  const handleOutputModeChange = (value) => {
    setFormData(prev => ({ ...prev, output_mode: value }));
    setShowDV360Config(value === 'DV360 Upload');
    
    // Clear DV360 config if not selected
    if (value !== 'DV360 Upload') {
      setFormData(prev => ({ ...prev, dv360_config_name: '' }));
    }
  };

  // Handle CSV file upload
  const handleCSVUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:text/csv;base64,
      
      // Update form data
      setFormData(prev => ({
        ...prev,
        keywords: '', // Clear text keywords
        keywords_type: 'file'
      }));
      
      // Store CSV data separately
      setCsvFile(base64);
      setCsvFileName(file.name);

      toast.success(`✓ ${file.name} uploaded successfully!`);
    };
    
    reader.onerror = () => {
      toast.error('Error reading file');
    };
    
    reader.readAsDataURL(file);
  };

  // Handle clearing CSV
  const handleClearCSV = () => {
    setCsvFile(null);
    setCsvFileName('');
    setFormData(prev => ({
      ...prev,
      keywords_type: 'text'
    }));
    
    // Clear file input
    document.getElementById('csv-upload').value = '';
  };

  // Download sample CSV
  const handleDownloadSampleCSV = () => {
    // Create CSV content matching mFilter's expected format
    // First row is ignored by mFilter, so we use it as header
    // Each row can have multiple words but NO COMMAS
    const csvContent = `Signals
MG Astor Review India
MG Astor ADAS Features
MG Astor First Drive
MG Astor Black Storm Edition
MG Astor Official Video
MG Astor AI Features
MG Astor 2024 Review
BMW High Beam Assist
BMW High Beam Explained
BMW Night Driving Feature
Electric SUV India Review
Best Electric SUV India
EV SUV Comparison India
Mahindra Electric SUV India
Electric Car India Review`;

    // Create blob
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    
    // Create download link
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', 'sample_keywords.csv');
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('✓ Sample CSV downloaded!');
  };

  // Validate form
  const validateForm = () => {
    // Required fields
    if (!formData.brief_name.trim()) {
      toast.error('Requirement Name is required');
      return false;
    }
    
    if (!formData.audience) {
      toast.error('Target audience is required');
      return false;
    }

    if (!formData.ad_creative) {
      toast.error('Ad creative link is required');
      return false;
    }

    if (!formData.language) {
      toast.error('Language is required');
      return false;
    }

    if (!formData.geo_country) {
      toast.error('Country is required');
      return false;
    }

    if (!formData.city) {
      toast.error('City is required');
      return false;
    }
    
    // Check keywords OR CSV
    if (!formData.keywords.trim() && !csvFile) {
      toast.error('Please enter keywords OR upload a CSV file');
      return false;
    }
    
    // Check DV360 config if DV360 mode
    if (showDV360Config && !formData.dv360_config_name) {
      toast.error('DV360 Configuration is required when Mode is DV360 Upload');
      return false;
    }
    
    return true;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    if (!validateForm()) return;
    
    setLoading(true);
    
    try {
      // Prepare payload
      const payload = {
        brief_name: formData.brief_name.trim(),
        ad_creative: formData.ad_creative.trim(),
        geo_country: formData.geo_country,
        city: formData.city.trim(),
        language: formData.language,
        audience: formData.audience.trim(),
        output_mode: formData.output_mode,
        type: formData.type,
        keywords_type: formData.keywords_type,
        remarks: formData.remarks.trim(),
        dv360_config_name: formData.dv360_config_name
      };
      
      // Add keywords or keywords_file based on type
      if (formData.keywords_type === 'file') {
        payload.keywords_file = csvFile;
      } else {
        payload.keywords = formData.keywords.trim();
      }
      
      // API call
      const response = await axios.post(
        `${BASE_URL}/frame_iq/api/campaign/create`,
        payload
      );
      
      // Success - check both HTTP status AND response.data.status
      if (response.status === 201 || response.status === 200 || response.data.status === true) {
        toast.success('Campaign created successfully!');
        
        // Wait a moment before navigating (so user sees the toast)
        setTimeout(() => {
          navigate('/campaign/list');
        }, 3000);
      } else {
        toast.error('Failed to create campaign: ' + (response.data.message || 'Unknown error'));
      }     
    } catch (error) {
      console.error('Error creating campaign:', error);
      const errorMessage = error.response?.data?.message || error.message;
      toast.error('Error creating campaign: ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle clear form
  const handleClearForm = () => {
    setFormData({
      brief_name: '',
      ad_creative: '',
      keywords: '',
      geo_country: '',
      city: '',
      language: '',
      audience: '',
      output_mode: 'direct',
      type: 'keyword',
      keywords_type: 'text',
      remarks: '',
      dv360_config_name: ''
    });
    setCsvFile(null);
    setCsvFileName('');
    setShowDV360Config(false);
    toast.success('Form cleared');
  };

  // ==================== RENDER ====================
  
  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <Toaster 
        position='top-right' 
        toastOptions={{
          duration: 2000,
          style: {
            background: '#363636',
            color: '#fff',
            padding: '16px',
            borderRadius: '8px',
          },
          success: {
            duration: 2000,
            iconTheme: {
              primary: '#10B981',
              secondary: '#fff',
            },
          },
          error: {
            duration: 2000,
            iconTheme: {
              primary: '#EF4444',
              secondary: '#fff',
            },
          },
        }}
      />
      
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/campaign/list')}
          className="text-blue-600 hover:text-blue-800 mb-2"
        >
          ← Back to List
        </button>
        <h1 className="text-2xl font-bold text-gray-800">Create New Campaign</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Section 1: Campaign Details */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Campaign Details</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Requirement Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requirement Name
              </label>
              <input
                type="text"
                value={formData.brief_name}
                onChange={(e) => handleInputChange('brief_name', e.target.value)}
                placeholder="Enter Campaign Name"
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={loading}
              />
            </div>

            {/* Mode of Output */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mode of Output
              </label>
              <select
                value={formData.output_mode}
                onChange={(e) => handleOutputModeChange(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={loading}
              >
                <option value="direct">Direct Download</option>
                <option value="DV360 Upload">DV360 Upload</option>
              </select>
            </div>

            {/* DV360 Configuration (conditional) */}
            {showDV360Config && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  DV360 Configuration
                </label>
                <input
                  type="text"
                  value={formData.dv360_config_name}
                  onChange={(e) => handleInputChange('dv360_config_name', e.target.value)}
                  placeholder="Enter DV360 Config Name"
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  disabled={loading}
                />
              </div>
            )}

            {/* Targeted Audience */}
            <div className={showDV360Config ? 'col-span-3' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Targeted Audience
              </label>
              <input
                type="text"
                value={formData.audience}
                onChange={(e) => handleInputChange('audience', e.target.value)}
                placeholder="e.g., Youth, Generic"
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={loading}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Ad Creative & Targeting */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Ad Creative & Targeting</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Ad Creative Link */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ad Creative Link
              </label>
              <input
                type="url"
                value={formData.ad_creative}
                onChange={(e) => handleInputChange('ad_creative', e.target.value)}
                placeholder="https://example.com/ad-image.jpg"
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={loading}
              />
            </div>

            {/* Language */}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-1'>
                Language
              </label>
              <input 
                type='text'
                value={formData.language}
                onChange={(e) => handleInputChange('language', e.target.value)}
                placeholder='e.g., English, Hindi'
                className='w-full border border-gray-300 rounded px-3 py-2'
                disabled={loading} 
              />
            </div>

            {/* Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                GEOs/Countries
              </label>
              <input 
                type='text'
                value={formData.geo_country}
                onChange={(e) => handleInputChange('geo_country', e.target.value)}
                placeholder='e.g., India, USA'
                className='w-full border border-gray-300 rounded px-3 py-2'
                disabled={loading}
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => handleInputChange('city', e.target.value)}
                placeholder="e.g., Mumbai, Delhi"
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={loading}
              />
            </div>

            {/* Type */}
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={formData.type}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2"
                disabled={loading}
              >
                <option value="keyword">Signal</option>
                <option value="channel">Channel</option>
              </select>
            </div>
          </div>
        </div>

        {/* Section 3: Keywords */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">
            Signals (Choose One Method)
          </h2>
          
          {/* Text Keywords */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Signals (Comma-Separated)
            </label>
            <textarea
              value={formData.keywords}
              onChange={(e) => handleInputChange('keywords', e.target.value)}
              placeholder="Enter signals separated by commas:
e.g., MG Astor Review India, BMW High Beam Assist, Electric SUV India"
              rows="3"
              className="w-full border border-gray-300 rounded px-3 py-2"
              disabled={loading || csvFile !== null}
            />
            <p className="text-xs text-gray-500 mt-1">
              💡 For text input: Use commas to separate signals
            </p>
          </div>

          {/* OR Divider */}
          <div className="flex items-center my-4">
            <div className="flex-grow border-t border-gray-300"></div>
            <span className="px-4 text-gray-500 text-sm font-medium">OR</span>
            <div className="flex-grow border-t border-gray-300"></div>
          </div>

          {/* CSV Upload */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Upload CSV File
              </label>
              {/* Download Sample CSV Button */}
              <button
                type="button"
                onClick={handleDownloadSampleCSV}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1 font-medium transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                </svg>
                Download Sample CSV
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              <input
                id="csv-upload"
                type="file"
                accept=".csv"
                onChange={handleCSVUpload}
                className="border border-gray-300 rounded px-3 py-2"
                disabled={loading || formData.keywords.trim() !== ''}
              />
              {csvFileName && (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-green-600 font-medium">✓ {csvFileName}</span>
                  <button
                    type="button"
                    onClick={handleClearCSV}
                    className="text-red-600 hover:text-red-800 text-sm font-medium"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
            
            {/* CSV Format Info Box */}
            <div className="mt-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex gap-2">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-sm text-blue-800 font-semibold mb-2">CSV Format Requirements:</p>
                  <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
                    <li>First row is used as header (e.g., "Signals") and will be ignored</li>
                    <li>One signal per row - signals can have multiple words</li>
                    <li><strong className="text-blue-900">DO NOT use commas within signals</strong></li>
                    <li>Each row is treated as one complete signal phrase</li>
                    <li>Example: "MG Astor Review India" is ONE signal</li>
                  </ul>
                  <p className="text-xs text-blue-600 mt-2 italic">
                    ⬆️ Click "Download Sample CSV" above to see the correct format
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Remarks */}
        <div className="bg-white rounded shadow p-6">
          <h2 className="text-lg font-semibold mb-4 text-gray-700">Additional Information</h2>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Remarks (Optional)
            </label>
            <textarea
              value={formData.remarks}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              placeholder="Enter any additional notes or remarks..."
              rows="4"
              className="w-full border border-gray-300 rounded px-3 py-2"
              disabled={loading}
            />
          </div>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={handleClearForm}
            className="px-6 py-2 border border-gray-300 rounded hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            Clear Form
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 transition-colors shadow-sm"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Campaign'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default FrameIQCreate;